import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "../customAxios.js";
import { generateRandomToken, capitalizeWords } from "../utils/functions.js";
import { getImagesUrls } from "../utils/s3Utils.js";

export function useCompanyDetails() {
  const { companyId } = useParams();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingCouriers, setSettingCouriers] = useState([]);
  const [availableCouriers, setAvailableCouriers] = useState([]);

  const fetchCompanyDetails = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const [
        companyResponse,
        sellerResponse,
        warehousesResponse,
        operatorsResponse,
        settingEcommerceResponse,
      ] = await Promise.all([
        axios.get(`companies/get-company/${companyId}/`),
        axios.get(`sellers/get-seller/${companyId}/`),
        axios.get(`warehouses/get_company_warehouses/${companyId}/`),
        axios.get(`operators/company/get-all/${companyId}/`),
        axios.get(`companies/admin/company/${companyId}/setting_ecommerces/`),
      ]);

      const imageskeyRoutes = {
        s3Woocommerce: "assets/images/woocommerce.png",
        s3Shopify: "assets/images/shopify.png",
        s3MercadoLibre: "assets/images/mercadolibre.png",
        s3Bootic: "assets/images/bootic.png",
        s3Prestashop: "assets/images/prestashop.png",
        s3Vtex: "assets/images/vtex.png",
      };
      const imageUrls = await getImagesUrls(imageskeyRoutes, "ecommerces");
      const rawIntegrations = settingEcommerceResponse.data || [];
      const settingEcommerces = rawIntegrations.map((ecommerce) => {
        ecommerce.image =
          imageUrls[`s3${capitalizeWords(ecommerce.name).split(" ").join("")}`];
        return ecommerce;
      });

      const seller = sellerResponse.data[0] || {};

      setCompany({
        id: companyResponse.data.id,
        rut: companyResponse.data.rut,
        fullName: `${seller?.user?.first_name} ${seller?.user?.last_name}`,
        exposedApiToken: companyResponse.data.exposed_api_token,
        socialReason: companyResponse.data.social_reason,
        kam: companyResponse.data.kam?.user?.email || "Sin KAM",
        createdAt: companyResponse.data.created_at,
        lastLogin: seller?.user?.last_login,
        plan: companyResponse.data.plan,
        seller: seller,
        warehouses: warehousesResponse.data.serialized_warehouses || [],
        operators: operatorsResponse.data.serialized_operators || [],
        setting_ecommerces: settingEcommerces || [],
      });
    } catch (error) {
      console.error(`Error al obtener datos de la compañia: ${error}`);
      setError(`Error al obtener datos de la compañia: ${error}`);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const fetchSettingCouriers = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await axios.get(
        `companies/admin/company/${companyId}/setting_couriers/`,
      );
      setSettingCouriers(data);
    } catch (error) {
      console.error("Error al cargar configuración de couriers:", error);
    }
  }, [companyId]);

  const fetchAvailableCouriers = useCallback(async () => {
    try {
      const { data } = await axios.get("companies/available_couriers/");
      setAvailableCouriers(data);
    } catch (error) {
      console.error("Error al cargar couriers disponibles:", error);
    }
  }, []);

  const createSettingCourier = useCallback(
    async (courier, configuration) => {
      const { data } = await axios.post(
        `companies/admin/company/${companyId}/setting_couriers/create/`,
        {
          setting_courier: { courier, configuration },
        },
      );
      setSettingCouriers((prev) => [...prev, data]);
      return data;
    },
    [companyId],
  );

  const updateSettingCourier = useCallback(
    async (settingCourierId, configuration) => {
      const { data } = await axios.patch(
        `companies/admin/company/${companyId}/setting_couriers/update/${settingCourierId}/`,
        {
          setting_courier: { configuration },
        },
      );
      setSettingCouriers((prev) =>
        prev.map((sc) => (sc.id === settingCourierId ? data : sc)),
      );
      return data;
    },
    [companyId],
  );

  const deleteSettingCourier = useCallback(
    async (settingCourierId) => {
      await axios.delete(
        `companies/admin/company/${companyId}/setting_couriers/delete/${settingCourierId}/`,
      );
      setSettingCouriers((prev) =>
        prev.filter((sc) => sc.id !== settingCourierId),
      );
    },
    [companyId],
  );

  const changeSellerPassword = useCallback(
    async (tempPassword) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post(`sellers/change_password/${company.seller.id}/`, {
          new_password1: tempPassword,
          new_password2: tempPassword,
          token: generateRandomToken(14),
        });
      } catch (error) {
        console.error(`Error al generar el codigo de seguridad: ${error}`);
        setError("Error al generar el codigo de seguridad");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [company],
  );

  const downloadSellerWarehouses = useCallback(async (sellerId) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `admins/download_seller_warehouses/${sellerId}/`,
      );
      console.log(data);
    } catch (error) {
      console.error(`No se pudo iniciar la descarga de bodegas: ${error}`);
      setError("No se iniciar la descarga de bodegas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanyDetails();
    fetchSettingCouriers();
    fetchAvailableCouriers();
  }, [fetchCompanyDetails, fetchSettingCouriers, fetchAvailableCouriers]);

  return {
    company,
    loading,
    error,
    settingCouriers,
    availableCouriers,
    setError,
    setLoading,
    changeSellerPassword,
    refetchCompanyDetails: fetchCompanyDetails,
    downloadSellerWarehouses,
    createSettingCourier,
    updateSettingCourier,
    deleteSettingCourier,
  };
}
